import { isValidElement, ReactNode, useEffect, useRef, useState } from 'react'
import { MarkdownBody } from '@design-system/components/MarkdownBody'

import { parseEmbedTokens } from '@/components/artifact-editor/parseEmbedTokens'
import { InsightCard } from '@/components/quick-study-results/InsightCard/InsightCard'
import { rasterizePlot } from '@/components/quick-study-results/plotImageExport'
import { Result } from '@/components/quick-study-results/quickStudyTypes'
import { Heading } from '@/components/ui/Heading'
import { cn } from '@/lib/utils'

import {
  fillPickedOptionChip,
  normalizeStudyDocQuote,
  StudyDocQuote,
} from './enrichStudyDocQuote'
import { PortraitMosaicQuote } from './PortraitMosaicStrip/PortraitMosaicQuoteBlock'
import { buildRowQuotes } from './StudyDocMagazineBand/buildRowQuotes'
import { highlightMagazineStripPersonas } from './StudyDocMagazineBand/highlightMagazineStripPersonas'
import { resolveMagazineBandSource, StudyDocStripLookup } from './StudyDocMagazineBand/resolveMagazineBandSource'
import {
  StudyDocFullBleedScope,
  StudyDocMagazineBand,
} from './StudyDocMagazineBand/StudyDocMagazineBand'
import { StudyDocPrintChrome } from './StudyDocPrintChrome'
import { StudyDocQuotesGallery } from './StudyDocQuotesGallery'

// Print projection is armed by `:has()` alone, so it must ship with the body
// that renders a report — not only with the explicit print button.
import './studyDocPrint.css'
import {
  isStudyDocDecisionMastheadVisible,
  StudyDocDecisionConfidence,
  StudyDocDecisionMasthead,
} from './StudyDocDecisionMasthead/StudyDocDecisionMasthead'

export type { StudyDocQuote } from './enrichStudyDocQuote'

export type StudyDocPlotLookup = Result | ReactNode

export type StudyDocBandKind = 'narrative' | 'plot' | 'quote' | 'masthead' | 'fullBleed'

export const STUDY_DOC_BAND_KINDS: Array<StudyDocBandKind> = [
  'narrative',
  'plot',
  'quote',
  'masthead',
  'fullBleed',
]

/**
 * Centered reading measure for StudyDoc lite-magazine and other markdown
 * artifact canvases (brief, desk research, report). Full-bleed mosaic bands
 * still escape this column via viewport geometry on the scroll host.
 */
export const STUDY_DOC_READING_COLUMN_CLASS =
  'mx-auto w-full max-w-[42rem] text-[1.05rem] leading-[1.7]'

export interface StudyDocBodyProps {
  markdown: string
  plots?: Record<string, StudyDocPlotLookup>
  quotes?: Record<string, StudyDocQuote>
  /** Placeholder label when a plot id is not a Result in `plots`. */
  plotLabels?: Record<string, string>
  layout?: 'lite-magazine'
  /** Thread study docs pass `'plot'` so InsightCard narrative is not repeated inside embeds. */
  plotRegion?: 'all' | 'plot'
  /** Rendered once, immediately before the first plot band (e.g. cut picker). */
  beforePlotBand?: ReactNode
  /** R2.4 fills this masthead shell. Absent → no masthead band. */
  masthead?: ReactNode
  /** P(best) + optional Fairgen. Absent → empty-stable, no masthead. */
  decisionConfidence?: StudyDocDecisionConfidence | null
  /** User off-switch. Hidden → same as absent. */
  hideDecisionMasthead?: boolean
  /** R5.3 fills this full-bleed shell. Absent → no full-bleed band. */
  fullBleed?: ReactNode
  /** FactBundle strip lookup. Optional — plot question names also resolve the band. */
  strips?: Record<string, StudyDocStripLookup>
  /** Slim picker population for mosaic tiles missing a row image. Never getPersonas. */
  populationId?: string
  /**
   * How far a full-bleed band escapes its host. `'card'` (default) keeps today's
   * card-relative bleed for every in-thread surface (peek, rail, expanded). `'viewport'`
   * is for the fullscreen report surface, where the band spans the whole reading surface
   * edge to edge. See {@link StudyDocFullBleedScope}.
   */
  fullBleedScope?: StudyDocFullBleedScope
  className?: string
}

export interface StudyDocBand {
  kind: StudyDocBandKind
  segments: Array<StudyDocBandSegment>
}

interface StudyDocTextSegment {
  kind: 'text'
  value: string
}

interface StudyDocPlotSegment {
  kind: 'plot'
  id: string
}

interface StudyDocQuoteSegment {
  kind: 'quote'
  id: string
}

interface StudyDocMastheadSegment {
  kind: 'masthead'
  id: string
}

interface StudyDocFullBleedSegment {
  kind: 'fullBleed'
  id: string
}

type StudyDocBandSegment =
  | StudyDocTextSegment
  | StudyDocPlotSegment
  | StudyDocQuoteSegment
  | StudyDocMastheadSegment
  | StudyDocFullBleedSegment

export function unknownStudyDocBandMessage(kind: string): string {
  return `Unknown study-doc band kind: ${kind}`
}

function bandKindFromSegmentKind(kind: string): StudyDocBandKind {
  if (kind === 'text') {
    return 'narrative'
  }
  if (kind === 'plot') {
    return 'plot'
  }
  if (kind === 'quote') {
    return 'quote'
  }
  if (kind === 'masthead') {
    return 'masthead'
  }
  if (kind === 'fullBleed' || kind === 'quotes_strip') {
    return 'fullBleed'
  }
  throw new Error(unknownStudyDocBandMessage(kind))
}

export function StudyDocBody(props: StudyDocBodyProps) {
  const {
    markdown,
    plots = {},
    quotes = {},
    plotLabels = {},
    layout = 'lite-magazine',
    plotRegion = 'all',
    beforePlotBand,
    masthead,
    decisionConfidence,
    hideDecisionMasthead,
    fullBleed,
    strips,
    populationId,
    fullBleedScope = 'card',
    className,
  } = props

  const bands = groupStudyDocBands(markdown)
  const isLiteMagazine = layout === 'lite-magazine'
  const firstPlotBandIndex = bands.findIndex((band) => band.kind === 'plot')
  const hasMastheadToken = bands.some((band) => band.kind === 'masthead')
  const hasFullBleedToken = bands.some((band) => band.kind === 'fullBleed')

  const decisionMasthead = isStudyDocDecisionMastheadVisible(
    decisionConfidence,
    hideDecisionMasthead,
  ) ? (
    <StudyDocDecisionMasthead
      decisionConfidence={decisionConfidence}
      isHidden={hideDecisionMasthead}
    />
  ) : null
  const resolvedMasthead = masthead ?? decisionMasthead

  const mastheadBand =
    resolvedMasthead != null && !hasMastheadToken ? (
      <section
        className="study-doc-masthead-band"
        data-testid="study-doc-masthead-band"
        data-study-doc-band="masthead"
      >
        {resolvedMasthead}
      </section>
    ) : null

  const fullBleedBand =
    fullBleed != null && !hasFullBleedToken ? (
      <section
        className="study-doc-full-bleed-band"
        data-testid="study-doc-full-bleed-band"
        data-study-doc-band="full-bleed"
      >
        {fullBleed}
      </section>
    ) : null

  return (
    <article
      className={cn(
        'study-doc-body space-y-8',
        isLiteMagazine &&
          cn('study-doc-lite-magazine', STUDY_DOC_READING_COLUMN_CLASS),
        className,
      )}
      data-testid="study-doc-body"
      data-study-doc-print-root=""
      data-layout={layout}
    >
      <StudyDocPrintChrome />
      {mastheadBand}
      {bands.map((band, bandIndex) => {
        if (band.kind === 'narrative') {
          return (
            <section
              key={`narrative-${bandIndex}`}
              className="space-y-4"
              data-testid="study-doc-narrative-band"
            >
              {band.segments.map((segment, segmentIndex) => {
                if (segment.kind !== 'text') {
                  return null
                }

                return (
                  <MarkdownBody
                    key={`text-${bandIndex}-${segmentIndex}`}
                    content={segment.value}
                  />
                )
              })}
            </section>
          )
        }

        if (band.kind === 'plot') {
          const plotSection = (
            <section
              key={`plots-${bandIndex}`}
              className="space-y-5"
              data-testid="study-doc-plots-band"
            >
              {band.segments.map((segment, segmentIndex) => {
                if (segment.kind !== 'plot') {
                  return null
                }

                return (
                  <StudyDocPlotEmbed
                    key={`plot-${segment.id}-${segmentIndex}`}
                    id={segment.id}
                    lookup={plots[segment.id]}
                    label={plotLabels[segment.id]}
                    region={plotRegion}
                    populationId={populationId}
                  />
                )
              })}
            </section>
          )

          if (bandIndex === firstPlotBandIndex && beforePlotBand) {
            return (
              <div key={`plots-with-cut-${bandIndex}`}>
                {beforePlotBand}
                {plotSection}
              </div>
            )
          }

          return plotSection
        }

        if (band.kind === 'quote') {
          const resolvedQuotes = band.segments.flatMap((segment) => {
            if (segment.kind !== 'quote') {
              return []
            }
            const quote = quotes[segment.id] ?? quotes[`quote:${segment.id}`]
            const named = quote ? normalizeStudyDocQuote(quote) : null
            if (!named || !named.text.trim()) {
              return []
            }
            return [{ id: segment.id, quote: named }]
          })

          if (resolvedQuotes.length === 0) {
            return null
          }

          return (
            <section
              key={`quotes-${bandIndex}`}
              className="space-y-4"
              data-testid="study-doc-quotes-band"
            >
              <StudyDocQuotesGallery
                entries={resolvedQuotes}
                populationId={populationId}
              />
            </section>
          )
        }

        if (band.kind === 'masthead') {
          return (
            <section
              key={`masthead-${bandIndex}`}
              className="study-doc-masthead-band"
              data-testid="study-doc-masthead-band"
              data-study-doc-band="masthead"
            >
              {resolvedMasthead}
            </section>
          )
        }

        if (band.kind === 'fullBleed') {
          const stripId = band.segments.find((segment) => segment.kind === 'fullBleed')?.id
          return (
            <section
              key={`full-bleed-${bandIndex}`}
              className="study-doc-full-bleed-band"
              data-testid="study-doc-full-bleed-band"
              data-study-doc-band="full-bleed"
            >
              {resolveFullBleedSlot(
                fullBleed,
                plots,
                stripId,
                strips,
                populationId,
                quotes,
                fullBleedScope,
              )}
            </section>
          )
        }

        throw new Error(unknownStudyDocBandMessage((band as StudyDocBand).kind))
      })}
      {fullBleedBand}
    </article>
  )
}

interface StudyDocPlotEmbedProps {
  id: string
  lookup?: StudyDocPlotLookup
  label?: string
  region?: 'all' | 'plot'
  /** Slim picker population for quote-card portraits missing a row image. */
  populationId?: string
}

function StudyDocPlotEmbed(props: StudyDocPlotEmbedProps) {
  const { id, lookup, label, region = 'all', populationId } = props
  const resolved = isStudyDocResult(lookup) ? lookup : null

  /*
   * Hooks live here, unconditionally, rather than in a separate
   * `<StudyDocResolvedPlotEmbed>` component rendered only from the
   * `resolved` branch below. A plot embed starts as the unresolved
   * placeholder while its run is still loading and switches to `resolved`
   * once data lands (see `ThreadDetailPage`'s foreign-run lookup) — if the
   * resolved branch returned a DIFFERENT component type there, React would
   * unmount the placeholder's DOM node and mount a brand new one on that
   * transition, silently detaching any `data-testid="study-doc-plot-embed"`
   * reference a caller queried before the swap. Every branch below instead
   * returns the SAME host `<div data-testid="study-doc-plot-embed">` at
   * this one call site, so React reconciles it as one continuously-updated
   * node across the whole unresolved → resolved lifecycle.
   */
  const chartRef = useRef<HTMLDivElement>(null)
  const [rasterUrl, setRasterUrl] = useState<string | null>(null)
  const [printAspect, setPrintAspect] = useState<string | null>(null)
  const [printHeight, setPrintHeight] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const node = chartRef.current
    if (!resolved || !node) {
      return
    }
    rasterizePlot(node)
      .then((blob) => {
        if (cancelled) {
          return
        }
        objectUrl = URL.createObjectURL(blob)
        setRasterUrl(objectUrl)
        const measured = node.offsetHeight
        if (measured > 0) {
          setPrintHeight(measured)
        }
        const probe = new Image()
        probe.onload = () => {
          if (cancelled || probe.naturalWidth <= 0 || probe.naturalHeight <= 0) {
            return
          }
          setPrintAspect(`${probe.naturalWidth} / ${probe.naturalHeight}`)
        }
        probe.src = objectUrl
      })
      .catch(() => {
        // Print CSS still projects the live embed if rasterization fails.
      })
    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
    // Rasterize once per resolved plot identity. `result` is a fresh object
    // reference on every render even when unchanged, so it is deliberately
    // excluded from deps — only `id`/`resolved` (the plot's stable identity
    // and its resolved-vs-not state) re-trigger. Accepted tradeoff: the
    // raster is a mount-time snapshot, so it does not track a later resize,
    // theme toggle, or cut change on the live chart — reports are static
    // once generated, so this drift is cheap to accept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resolved])

  if (resolved) {
    return (
      <div data-testid="study-doc-plot-embed" data-plot-id={id}>
        <div ref={chartRef} className={rasterUrl ? 'print:hidden' : undefined}>
          <InsightCard
            result={resolved}
            chrome="plot-card"
            region={region}
            populationId={populationId}
          />
        </div>
        {rasterUrl ? (
          <div
            role="img"
            aria-hidden
            data-testid="study-doc-plot-print-image"
            data-print-src={rasterUrl}
            className="hidden w-full print:block"
            style={{
              height: printAspect || !printHeight ? undefined : `${printHeight}px`,
              aspectRatio: printAspect ?? undefined,
              backgroundImage: `url("${rasterUrl}")`,
              backgroundPosition: 'top center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% auto',
            }}
          />
        ) : null}
      </div>
    )
  }

  if (isValidElement(lookup)) {
    return (
      <div data-testid="study-doc-plot-embed" data-plot-id={id}>
        {lookup}
      </div>
    )
  }

  const placeholderLabel =
    (typeof lookup === 'string' && lookup.length > 0 ? lookup : null) ??
    label ??
    id

  return (
    <div
      className="my-1 rounded-xl border border-border bg-muted/40 px-4 py-8 text-center"
      data-testid="study-doc-plot-embed"
      data-plot-id={id}
    >
      <Heading level={3} size="sm" weight="medium" className="font-sans">
        Plot: {placeholderLabel}
      </Heading>
    </div>
  )
}

/**
 * One inline quote citation as the analyst leaks it into prose: the same
 * `quote:{questionName}:{n}` key `composeThreadStudyDoc.ts` stamps on every doc quote
 * (id charset matches `parseEmbedTokens`'s), with the trailing `:{n}` REQUIRED so plain
 * prose that happens to contain "quote:" is never eaten.
 */
const INLINE_QUOTE_REF_SOURCE = 'quote:[A-Za-z0-9][A-Za-z0-9._:-]*:\\d+'

// Leading same-line whitespace is consumed WITH the citation, so `first" (quote:…:3).`
// collapses cleanly to `first".` and a mid-sentence bare ref never leaves a double space.
// Nothing outside the citation itself is ever rewritten.
const PARENTHESIZED_QUOTE_REFS_RE = new RegExp(
  `[^\\S\\n]*\\(\\s*${INLINE_QUOTE_REF_SOURCE}(?:\\s*[,;]\\s*${INLINE_QUOTE_REF_SOURCE})*\\s*\\)`,
  'g',
)
const BARE_QUOTE_REF_RE = new RegExp(`[^\\S\\n]?${INLINE_QUOTE_REF_SOURCE}`, 'g')

/**
 * Strip inline quote-key citations the analyst sometimes writes into prose — e.g.
 * `... in client conversations first" (quote:62e90ddc-…:3).` — which are NOT the
 * `{{quote:id}}` embed grammar `parseEmbedTokens` consumes, so they leak verbatim into
 * the rendered document (live QA 2026-09-03). The verbatims those keys point at are
 * already quoted in the surrounding sentence; the raw key is pure noise to a reader.
 * Generic over any survey: this matches the key GRAMMAR only, never a question id.
 */
export function stripInlineQuoteRefs(text: string): string {
  return text.replace(PARENTHESIZED_QUOTE_REFS_RE, '').replace(BARE_QUOTE_REF_RE, '')
}

export function collectStudyDocBands(
  segments: Array<{ kind: string; id?: string; value?: string }>,
): Array<StudyDocBand> {
  const bands: Array<StudyDocBand> = []

  segments.forEach((segment) => {
    const bandKind = bandKindFromSegmentKind(segment.kind)

    if (bandKind === 'narrative') {
      const value = stripInlineQuoteRefs(segment.value ?? '')
      if (value.trim().length === 0) {
        return
      }

      const last = bands[bands.length - 1]
      if (last?.kind === 'narrative') {
        last.segments.push({ kind: 'text', value })
        return
      }

      bands.push({ kind: 'narrative', segments: [{ kind: 'text', value }] })
      return
    }

    const embed = {
      kind: bandKind,
      id: segment.id ?? '',
    } as StudyDocPlotSegment | StudyDocQuoteSegment | StudyDocMastheadSegment | StudyDocFullBleedSegment

    const last = bands[bands.length - 1]
    if (last?.kind === bandKind) {
      last.segments.push(embed)
      return
    }

    bands.push({ kind: bandKind, segments: [embed] })
  })

  return bands
}

function groupStudyDocBands(markdown: string): Array<StudyDocBand> {
  return collectStudyDocBands(parseEmbedTokens(markdown))
}

function isStudyDocResult(value: unknown): value is Result {
  if (!value || typeof value !== 'object' || isValidElement(value)) {
    return false
  }

  const candidate = value as Record<string, unknown>

  return typeof candidate.plot === 'string' && typeof candidate.title === 'string'
}

/** Cap per the plan (S7): rotation only ever needs a handful of quotes. */
const MAGAZINE_BAND_QUOTE_CAP = 6

/**
 * The magazine band's `PortraitMosaicQuoteBlock` renders `attribution` verbatim as the
 * whole identity line (see its own doc comment: "Name, age · role · city") — it never
 * reads `blurb` separately, unlike `StudyDocQuotesGallery`'s cards, which show name and
 * blurb in their own slots. `normalizeStudyDocQuote` leaves `attribution` as the bare
 * name for every caller, so join the identity blurb in here, only for what feeds the
 * band. Blurb absent → attribution stays exactly the name (today's output, unchanged).
 */
function withMagazineBandAttribution(quote: StudyDocQuote): StudyDocQuote {
  const blurb = (quote.blurb || '').trim()
  if (!blurb) {
    return quote
  }
  return { ...quote, attribution: `${quote.name}, ${blurb}` }
}

/**
 * Reuses the `quote:{questionName}:{n}` key prefix `composeThreadStudyDoc.ts`
 * already stamps on every quote (plus the bare-id alias) — no new join.
 */
/**
 * Every Result embedded in this document, ordered so the band's own featured question
 * comes first. Feeds {@link fillPickedOptionChip}: the band's cite names what a speaker
 * picked, and on a follow-up open end that pick lives in a DIFFERENT result of the same
 * document (see that function's note). Featured-first keeps the answer to the question the
 * band is actually about winning over any other choice question in the doc.
 */
function studyDocResultsForChip(
  plots: Record<string, StudyDocPlotLookup>,
  featuredQuestionName: string | undefined,
): Array<Result> {
  const questionName = (featuredQuestionName || '').trim()
  const results = Object.values(plots).filter(isStudyDocResult)
  const featured = results.filter(
    (result) => questionName.length > 0 && result.question?.name === questionName,
  )
  return [...featured, ...results.filter((result) => !featured.includes(result))]
}

function quotesForFeaturedQuestion(
  quotes: Record<string, StudyDocQuote>,
  featuredQuestionName: string | undefined,
  chipResults: Array<Result>,
): Array<StudyDocQuote> {
  const questionName = (featuredQuestionName || '').trim()
  if (!questionName) {
    return []
  }
  const prefix = `quote:${questionName}:`
  const matched: Array<StudyDocQuote> = []
  const seen = new Set<StudyDocQuote>()
  Object.entries(quotes).forEach(([key, quote]) => {
    if (!key.startsWith(prefix) || seen.has(quote)) {
      return
    }
    const named = normalizeStudyDocQuote(quote)
    if (!named || !named.text.trim()) {
      return
    }
    seen.add(quote)
    matched.push(fillPickedOptionChip(withMagazineBandAttribution(named), chipResults))
  })
  return matched.slice(0, MAGAZINE_BAND_QUOTE_CAP)
}

/**
 * Row-sourced quotes not already covered by a curated one for the same persona. Curated
 * quotes win: a respondent whose answer was hand-picked for the highlight reel is never
 * duplicated by their own raw row (same text, unstyled attribution).
 */
function additionalRowQuotes(
  curated: Array<StudyDocQuote>,
  rowQuotes: Array<PortraitMosaicQuote>,
): Array<PortraitMosaicQuote> {
  const curatedPersonaIds = new Set(
    curated.map((quote) => (quote.personaId || '').trim()).filter(Boolean),
  )
  return rowQuotes.filter((quote) => {
    const personaId = (quote.personaId || '').trim()
    return !personaId || !curatedPersonaIds.has(personaId)
  })
}

function resolveFullBleedSlot(
  fullBleed: ReactNode,
  plots: Record<string, StudyDocPlotLookup>,
  stripId: string | undefined,
  strips: Record<string, StudyDocStripLookup> | undefined,
  populationId: string | undefined,
  quotes: Record<string, StudyDocQuote>,
  fullBleedScope: StudyDocFullBleedScope,
): ReactNode {
  if (fullBleed != null) {
    return fullBleed
  }

  const source = resolveMagazineBandSource(plots, stripId, strips)
  if (!source) {
    return null
  }

  const chipResults = studyDocResultsForChip(plots, source.featuredQuestionName)
  const curatedQuotes = quotesForFeaturedQuestion(quotes, source.featuredQuestionName, chipResults)

  // KPI mode is untouched: its supporting quote is picked from the curated set only
  // (`PortraitMosaicStrip`'s `kpiQuote` lookup), so row quotes never enter the mix there.
  const isKpiMode = source.mode === 'kpi'
  // `chipResults` is reused as the row-quote result pool too: every Result in the
  // document, band's own featured result first when its name resolves cleanly (see
  // `buildRowQuotes.ts` for why the verbatim and the tile panel can live on two
  // different results of the same doc, live QA thread 5bfa8a9d-ffd8-4ec2-abec-9285b9fd947a).
  const rowQuotes = isKpiMode
    ? []
    : buildRowQuotes(chipResults, source.featuredQuestionName ?? '', chipResults)
  const additionalQuotes = isKpiMode ? [] : additionalRowQuotes(curatedQuotes, rowQuotes)
  // Rotation stays scoped to the curated reel; when nothing was curated, rotate the first
  // MAGAZINE_BAND_QUOTE_CAP row quotes instead of the (possibly much larger) full set.
  const rotationCount = isKpiMode
    ? undefined
    : curatedQuotes.length > 0
      ? curatedQuotes.length
      : Math.min(MAGAZINE_BAND_QUOTE_CAP, curatedQuotes.length + additionalQuotes.length)

  return (
    <StudyDocMagazineBand
      title={source.title}
      // No standfirst: the band is a quotes callout, not an analysis slot (live review
      // 2026-09-03). `source.standfirst` still exists for other consumers.
      responses={source.responses}
      highlightedPersonaIds={highlightMagazineStripPersonas(source.responses, {
        featuredQuestionName: source.featuredQuestionName,
        activeCutIds: source.activeCutIds,
      })}
      populationId={populationId}
      quotes={curatedQuotes}
      additionalQuotes={additionalQuotes}
      rotationCount={rotationCount}
      mode={source.mode}
      kpi={source.kpi}
      fullBleedScope={fullBleedScope}
    />
  )
}
